// =============================================================================
// io/sync/saveReadiness.ts — the readiness gate write.
// -----------------------------------------------------------------------------
// FOUR COLUMNS: the composite key, `readiness_items` and `readiness_completed_at`.
//
// ── WHY THIS IS THE SIMPLEST OF THE FOUR JOURNAL WRITERS ──
//
// The plan and contract writers merge over a stored blob because their JSONB
// carries keys this client does not model. `readiness_items` is different: it is
// a FLAT `{itemKey: boolean}` map with no nested structure and no unknown-key
// hazard — and `applyReadinessToggle` already carries orphaned keys from removed
// items through, so the object handed here is complete by construction.
//
// ⚠ That is an argument about THIS column's shape, not a general licence. If
// `readiness_items` ever grows structure, this writer owes the same merge.
//
// ── WHAT IT DOES NOT WRITE ──
//
// Not `readiness_groups` — that is the ITEM DEFINITIONS, it lives on
// `user_preferences`, and it belongs to the gear rather than to a tick. Not the
// plan columns, not the recap columns, not `session_contract`. Same rule as every
// other writer here: never send a column this module does not own.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const READINESS_COLUMNS = [
  'user_id', 'date', 'readiness_items', 'readiness_completed_at',
] as const;

export interface SaveReadinessResult {
  updatedAt: string | null;
  error: string | null;
  heldStale: boolean;
}

export interface SaveReadinessArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  items: Readonly<Record<string, boolean>>;
  /** ISO stamp, or null when the gate is not (or no longer) complete. */
  completedAt: string | null;
  baselineUpdatedAt: string | null;
}

export function readinessRow(
  date: string,
  items: Readonly<Record<string, boolean>>,
  completedAt: string | null,
  userId: string,
): Record<string, unknown> {
  return {
    user_id: userId,
    date,
    readiness_items: { ...items },
    // ⚠ NULL is a real value here, not "leave alone". Un-ticking an item after
    // the gate closed must clear the stamp, and PostgREST writes the null.
    readiness_completed_at: completedAt,
  };
}

export async function saveReadiness(args: SaveReadinessArgs): Promise<SaveReadinessResult> {
  const { sb, userId, date, items, completedAt, baselineUpdatedAt } = args;
  const row = readinessRow(date, items, completedAt, userId);

  // ⚠ FAIL-OPEN stale check, identical to the other three journal writers.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day's checklist was changed somewhere else after you loaded it — ` +
            `probably the other client or another tab. Nothing was saved, so neither ` +
            `version is lost. Reload to see the newer copy, then re-tick.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert(row, { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs(
        'save readiness', error, `Save failed: ${error.message}`,
      ),
    };
  }

  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, ` +
        `so it cannot be confirmed. Reload before ticking again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return { updatedAt: typeof ts === 'string' ? ts : null, heldStale: false, error: null };
}
